import * as React from 'react';

interface FilterColumn {
  field: string;
  text: string;
}

interface Props {
  filters: any;
  columns: FilterColumn[];
  valueLabels?: { [field: string]: { [value: string]: string } };
  onRemove: (field: string) => void;
  onClear: () => void;
}

function getChipValue(value: any): string {
  if (typeof value === 'undefined' || value === null || value === '') return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value !== 'object') return String(value);

  const from = value.from || value.start || value.dateFrom;
  const to = value.to || value.end || value.dateTo;
  if (from || to) return [from, to].filter(Boolean).join(' – ');
  if (typeof value.value !== 'undefined') return getChipValue(value.value);
  return '';
}

export function countActivePeopleFilters(filters: any): number {
  return Object.keys(filters || {}).filter(field => !isToolbarSearch(field) && Boolean(getChipValue(filters[field])))
    .length;
}

function isToolbarSearch(field: string): boolean {
  return ['nameSearch', 'firstName', 'lastName', 'requester.firstName', 'requester.lastName'].indexOf(field) !== -1;
}

export function PeopleFilterChips({
  filters,
  columns,
  valueLabels = {},
  onRemove,
  onClear
}: Props): React.ReactElement | null {
  const labels = columns.reduce((result, column) => {
    result[column.field] = column.text;
    return result;
  }, {});
  const chips = Object.keys(filters || {})
    .map(field => {
      const rawValue = filters[field];
      if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        return { field, value: getChipValue(rawValue) };
      }
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      const labelsForField = valueLabels[field] || {};
      const mappedValue = values
        .filter(value => value !== '' && typeof value !== 'undefined' && value !== null)
        .map(value => labelsForField[String(value)] || String(value))
        .join(', ');
      return { field, value: mappedValue || getChipValue(rawValue) };
    })
    .filter(chip => Boolean(chip.value));

  if (chips.length === 0) return null;

  return (
    <div className="people-filterChips">
      {chips.map(chip => (
        <span key={chip.field}>
          <b>{labels[chip.field] || chip.field.replace(/^requester\./, '')}:</b> {chip.value}
          <button
            type="button"
            onClick={() => onRemove(chip.field)}
            aria-label={`Remove ${labels[chip.field] || chip.field}`}
          >
            ×
          </button>
        </span>
      ))}
      <button type="button" className="people-filterChips__clear" onClick={onClear}>
        Clear all
      </button>
    </div>
  );
}
